import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { HiMenu } from "react-icons/hi"; // Menu Icon
import { twMerge } from "tailwind-merge";
import Darklogo from "../../assets/Sidebar/Dark_logo.png";
import Shortlogo from "../../assets/Sidebar/Logo_short.png";
import avatar from "../../assets/Icons/user-avatar.png";
import { currencyFormatter } from "../../utils/formatting";
import showToast from "../../utils/toastNotifications";
import { errorMessages } from "../../constants/frontendErrorMessages";
import { getUserWallet } from "../../store";
import { useThunk } from "../../hooks/useThunk";
import Notifications from "./Notifications";
import SupportChatButton from "./SupportChatButton";

const PRICE_NOTICE_STORAGE_KEY = "short_deadline_price_notice_v1";

const Navbar = ({ toggleSidebar, isSidebarOpen }) => {
 
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useSelector((state) => state.auth.user);
  const wallet = useSelector((state) => state.user.wallet); // wallet
  const isWalletFetching = useSelector((state) => state.user.isWalletFetching); // wallet isFetching state
  const [isMissingOrMalformed, setIsMissingOrMalformed] = useState(false);
  const [showPriceNotice, setShowPriceNotice] = useState(() => {
    try {
      return window.localStorage.getItem(PRICE_NOTICE_STORAGE_KEY) !== "dismissed";
    } catch {
      return true;
    }
  });
  const clientId = userData?.userId || userData?.customerId || userData?._id || "";

  const [doGetUserWallet] = useThunk(getUserWallet);
  
  const handleProfileNavigation = () => {
    if (location.pathname !== "/edit-profile") {
      navigate("/edit-profile");
    }
  };

  //fetch user Wallet
  useEffect(() => {
    fetchUserWallet();
  }, []);

  const fetchUserWallet = async () => {
    const result = await doGetUserWallet();
    if (!result.success) {
      showToast("error", result?.error?.message || errorMessages.ERROR_OCCURRED)
    }
  };

  const dismissPriceNotice = () => {
    try {
      window.localStorage.setItem(PRICE_NOTICE_STORAGE_KEY, "dismissed");
    } catch {
      // Keep dismissal working for this visit when browser storage is unavailable.
    }
    setShowPriceNotice(false);
  };

  return (
    <nav
      className={twMerge(
        `h-24 flex flex-col min-w-[320px] bg-white fixed top-0 transition-all duration-300 z-20 w-full xl:w-[calc(100vw-256px)] shadow-[0_10px_22px_rgba(15,23,42,0.12)]`,
        isSidebarOpen ? "ml-0" : ""
      )}
    >
      {showPriceNotice && (
        <div
          className="relative flex h-7 min-h-7 w-full items-center justify-center bg-blue-shades px-9 text-center font-inter text-[11px] leading-none text-white sm:text-xs"
          role="status"
        >
          <span className="sm:hidden"><strong>Short-deadline prices:</strong> up to 44% lower.</span>
          <span className="hidden sm:inline"><strong>Short-deadline prices are now up to 44% lower</strong> — plus your usual discounts.</span>
          <button
            type="button"
            onClick={dismissPriceNotice}
            className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-base leading-none text-white/90 hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/80"
            aria-label="Dismiss price update"
          >
            ×
          </button>
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-row items-center justify-between w-full px-4 xl:px-8">
        {/* Menu Icon and Logo for Small Screens */}
        <div className="flex flex-row xl:hidden items-center gap-4">
          <button
            onClick={toggleSidebar}
            className="text-primary text-2xl xl:hidden"
          >
            <HiMenu size={32} />
          </button>
          {/* <img
            src={Darklogo}
            alt="Logo"
            className="h-6 min-[350px]:h-8 w-auto object-contain block xl:hidden"
          /> */}
          <img
            src={Darklogo}
            alt="Logo"
            className="h-8 w-auto object-contain hidden sm:block xl:hidden"
          />
          <img
            src={Shortlogo}
            alt="Logo"
            className="h-8 w-auto object-contain block sm:hidden"
          />
        </div>

        {/* Title */}
        <div className="hidden xl:block">
          <h1 className="text-primary flex items-start font-roboto font-semibold text-[24px] ml-4 leading-5">
            {location.pathname === "/" ? "Dashboard" :
              (location.pathname === "/courses" || location.pathname.startsWith("/courses/")) ? "My Courses & Projects" :
              location.pathname === "/calculation" ? "Calculation" :
                location.pathname === "/raise-complaint" ? "Completed Orders" :
                  [location.pathname?.split("/")[1]]
                    ?.filter(Boolean)
                    ?.map(
                      (word) =>
                        word
                          .split("-") // Split by hyphen
                          .map((subWord) =>
                            subWord.replace(subWord[0], subWord[0]?.toUpperCase())
                          ) // Capitalize each subword
                          .join(" ") // Join the subwords with a space
                    )
                    .join(" ")}
          </h1>
        </div>

        {/* Right Section */}
        <div className="h-[48px] flex items-center justify-center gap-3 mt-2 bg-white rounded-full ">
          <SupportChatButton />

          {/* wallet amount */}
          <div className="py-2 px-2 sm:px-4 bg-light-blue rounded-[8px]">
            <h1 className="font-roboto text-level-3 font-medium text-blue-shades">
              {isWalletFetching ? (
                <ClipLoader
                  loading={isWalletFetching}
                  speedMultiplier={0.5}
                  size={22}
                  color="#60a5fa"
                />
              ) : wallet?.amount == null || wallet?.amount === "" ? (
                "$00.00"
              ) : (
                currencyFormatter.format(wallet?.amount || 0.0) || "$ 00.00"
              )}
            </h1>
          </div>

          <Notifications/>
          <div
            className="flex items-center hover:cursor-pointer bg-white"
            onClick={handleProfileNavigation}
          >
            <div className="hidden md:flex flex-col justify-start items-start mr-2 sm:mr-4">
              <span className="text-secondary font-inter font-medium text-level-5 leading-[13px] whitespace-nowrap">
                Client ID: {clientId || "-"}
              </span>
            </div>
            <img
              src={
                isMissingOrMalformed || !userData?.profilePicture
                  ? avatar
                  : userData?.profilePicture
              }
              alt="User Profile"
              onError={() => {
                setIsMissingOrMalformed(true);
              }}
              className="w-8 h-8 rounded-full object-cover"
            />
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
